import { Button } from "~/components/ui/button";
import { ArrowRightIcon, ChevronDownIcon, FolderClosedIcon, Trash2Icon, HashIcon, ImageOff, Lock } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "~/components/ui/card";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut } from "~/components/ui/dropdown-menu";
import { RaindropCollectionData, RaindropData, DewData } from "~/dataType";
import { Link } from "@remix-run/react";
import { formatDate, formatDateTime } from "~/lib/datetime";
import { useState, memo, useMemo, useCallback } from "react";
import { SUBSCRIPTION } from "~/lib/constants";

export const Dew = memo(({ dews, raindrops, currentDew, collections, isDemo = false, previewRaindrops = [], showBlurPreview = false, totalAvailable = 0 }: {
  dews: DewData[],
  raindrops: RaindropData[],
  currentDew?: DewData,
  collections: RaindropCollectionData[],
  isDemo?: boolean,
  previewRaindrops?: RaindropData[],
  showBlurPreview?: boolean,
  totalAvailable?: number,
}) => {
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const getCollection = useCallback((collectionId: number): RaindropCollectionData | null => {
    const collection = collections.find(c => c.raindropCollectionId === collectionId);
    if (!collection) {
      return null;
    }
    return collection;
  }, [collections]);

  // 全raindropからユニークなタグ一覧を抽出
  const uniqueTags = useMemo(() => {
    const tagSet = new Set<string>();
    raindrops.forEach(raindrop => {
      raindrop.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [raindrops]);

  // タグごとのraindrop数をカウント
  const getTagCount = useCallback((tag: string) => {
    return raindrops.filter(raindrop => raindrop.tags.includes(tag)).length;
  }, [raindrops]);

  const filteredRaindrops = useMemo(() => {
    if (selectedCollectionId !== null) {
      return raindrops.filter(raindrop => raindrop.collectionId === selectedCollectionId);
    }
    if (selectedTag !== null) {
      return raindrops.filter(raindrop => raindrop.tags.includes(selectedTag));
    }
    return raindrops;
  }, [selectedCollectionId, selectedTag, raindrops]);

  const getRaindropCount = useCallback((collectionId: number | null) => {
    if (collectionId === null) {
      return raindrops.length;
    }
    return raindrops.filter(raindrop => raindrop.collectionId === collectionId).length;
  }, [raindrops]);

  // フィルタリセット処理
  const handleAllFilter = useCallback(() => {
    setSelectedCollectionId(null);
    setSelectedTag(null);
  }, []);

  // コレクション選択時の処理
  const handleCollectionSelect = useCallback((collectionId: number) => {
    setSelectedCollectionId(collectionId);
    setSelectedTag(null);
  }, []);

  // タグ選択時の処理
  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTag(tag);
    setSelectedCollectionId(null);
  }, []);

  return (
    <>
      <DewHeader dews={dews} currentDew={currentDew} isDemo={isDemo} />

      {(collections.length > 0 || raindrops.length > 1 || uniqueTags.length > 0) && (
        <div className="flex items-center justify-center w-full mb-4 mt-8">
          <div className="flex flex-wrap flex-row gap-2 items-center">
            {/* All ボタン */}
            <Button
              variant="outline"
              className={`rounded-md font-normal text-xs px-2 py-1 min-w-14 justify-center hover:bg-slate-100 hover:text-gray-700 ${selectedCollectionId === null && selectedTag === null ? 'bg-slate-100 text-gray-700' : 'text-gray-500'}`}
              onClick={handleAllFilter}
            >
              All <span className="text-gray-400">({raindrops.length})</span>
            </Button>

            {/* コレクションボタン */}
            {collections
              .filter(collection => getRaindropCount(collection.raindropCollectionId) > 0)
              .map((collection) => (
                <Button
                  key={collection.raindropCollectionId}
                  variant="outline"
                  className={`rounded-md text-gray-500 font-normal text-xs py-1 px-2 min-w-14 justify-center hover:bg-slate-100 hover:text-gray-700 ${selectedCollectionId === collection.raindropCollectionId ? 'bg-slate-100 text-gray-700' : ''}`}
                  onClick={() => handleCollectionSelect(collection.raindropCollectionId)}
                >
                  <FolderClosedIcon className="w-[15px] h-[15px]" />
                  {collection.title} <span className="text-gray-400">({getRaindropCount(collection.raindropCollectionId)})</span>
                </Button>
              ))}

            {/* 削除されたアイテムボタン */}
            {raindrops.some(raindrop => raindrop.collectionId === -100) && (
              <Button
                variant="outline"
                className={`rounded-md text-gray-500 font-normal text-xs px-2 py-1 min-w-14 justify-center hover:bg-slate-100 hover:text-gray-700 ${selectedCollectionId === -100 ? 'bg-slate-100 text-gray-700' : ''}`}
                onClick={() => handleCollectionSelect(-100)}
              >
                <Trash2Icon className="w-[15px] h-[15px]" />
                Removed <span className="text-gray-400">({getRaindropCount(-100)})</span>
              </Button>
            )}

            {/* タグボタン */}
            {uniqueTags.map((tag) => (
              <Button
                key={tag}
                variant="outline"
                className={`rounded-md text-gray-500 font-normal text-xs py-1 px-2 min-w-14 justify-center hover:bg-slate-100 hover:text-gray-700 ${selectedTag === tag ? 'bg-slate-100 text-gray-700' : ''}`}
                onClick={() => handleTagSelect(tag)}
              >
                <HashIcon className="w-[15px] h-[15px]" />
                {tag} <span className="text-gray-400">({getTagCount(tag)})</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      <div>
        {filteredRaindrops.length > 0 ? (
          <div className={`grid lg:grid-cols-4 md:grid-cols-4 gap-4`}>
            {filteredRaindrops.map((raindrop: RaindropData, index: number) => (
              <RaindropCard
                key={`${raindrop.raindropId}-${index}`}
                raindrop={raindrop}
                getCollection={getCollection}
                isDemo={isDemo}
              />
            ))}

            {/* ブラープレビューカード: フィルター未選択時のみ表示 */}
            {showBlurPreview && selectedCollectionId === null && selectedTag === null && previewRaindrops.length > 0 && (
              <>
                <div className="col-span-full flex items-center gap-3 py-4" data-testid="blur-preview-separator">
                  <div className="flex-1 border-t border-gray-200" />
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Lock className="h-4 w-4" />
                    <span>{totalAvailable - raindrops.length} more with Deep Dive</span>
                  </div>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
                {previewRaindrops.map((raindrop: RaindropData, index: number) => (
                  <LockedRaindropCard
                    key={`locked-${raindrop.raindropId}-${index}`}
                    raindrop={raindrop}
                  />
                ))}
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="text-gray-400 text-lg mb-2">No raindrops found</div>
              <div className="text-gray-500 text-sm">
                {selectedCollectionId
                  ? "No bookmarks in this collection"
                  : "You might not have bookmarked anything on Raindrop.io yet"}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});
Dew.displayName = 'Dew';

const RaindropCard = memo(({ raindrop, getCollection, isDemo = false }: {
  raindrop: RaindropData;
  getCollection: (collectionId: number) => RaindropCollectionData | null;
  isDemo?: boolean;
}) => {
  const [imageError, setImageError] = useState(false);

  const FallbackImage = () => (
    <div className="absolute top-0 left-0 w-full h-full bg-gray-200 rounded-t-lg flex items-center justify-center">
      <div className="text-center text-gray-300">
        <ImageOff className="w-8 h-8 mx-auto" />
      </div>
    </div>
  );

  return (
    <Card className='flex flex-col'>
      <CardHeader className={'p-0'}>
        <Link
          className="relative w-full h-full bg-gray-300 rounded-t-lg"
          to={raindrop.link}
          target='_blank'
          rel="noreferrer"
          style={{
            aspectRatio: '16/9'
          }}
        >
          {raindrop.cover && !imageError ? (
            <img
              src={raindrop.cover}
              alt={raindrop.title}
              className="absolute top-0 left-0 w-full h-full object-cover rounded-t-lg"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : raindrop.cover ? (
            <FallbackImage />
          ) : (<FallbackImage />)}
        </Link>
      </CardHeader>
      <CardContent className={`${raindrop.collectionId === -100 ? 'bg-gray-100' : 'bg-gray-white'} flex-grow flex flex-col px-4 pt-4 pb-3`}>
        <div className="font-semibold text-[15px]">
          <Link
            to={raindrop.link}
            target='_blank'
            rel="noreferrer"
            className="line-clamp-5"
          >
            {raindrop.collectionId === -100 ? (
              <span className="line-through text-gray-400">{raindrop.title}</span>
            ) : (
              raindrop.title
            )}
          </Link>
        </div>
        <div className=" text-sm text-gray-400 mt-1">{raindrop.domain}</div>
        {raindrop.excerpt && (
          <div className="text-xs text-gray-500 mt-3 leading-relaxed line-clamp-3">
            {raindrop.excerpt}
          </div>
        )}
        {raindrop.tags && raindrop.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {raindrop.tags.slice(0, 3).map((tag, index) => {
              // スペースを含むタグは#も含めてダブルクォーテーションで囲む
              const searchQuery = tag.includes(' ') ? `"#${tag}"` : `#${tag}`;
              return (
                <a
                  key={index}
                  href={`https://app.raindrop.io/my/0/${encodeURIComponent(searchQuery)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full hover:bg-gray-200 cursor-pointer transition-colors"
                >
                  #{tag}
                </a>
              );
            })}
            {raindrop.tags.length > 3 && (
              <span className="inline-block text-gray-400 text-xs px-2 py-1">
                +{raindrop.tags.length - 3} more
              </span>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className={`${raindrop.collectionId === -100 ? 'bg-gray-100' : 'bg-gray-white'} border-t py-2 px-4`}>
        {raindrop.collectionId === -100 && (
          <div className='flex gap-2 items-center w-full justify-center text-xs text-gray-600 font-semibold'>
            Removed from Raindrop
          </div>
        )}

        {raindrop.collectionId !== -100 && (
          <div className='flex gap-2 items-center w-full'>
            <div className="text-xs text-gray-400">
              {formatDate(new Date(raindrop.created))}
            </div>
            <div className="ml-auto">
              {getCollection(raindrop.collectionId) && (
                isDemo ? (
                  <div className=" text-gray-500 border-gray-300 border rounded-md px-2 py-1 text-xs items-center gap-2 inline-flex">
                    <FolderClosedIcon className="w-[15px] h-[15px]" />
                    {getCollection(raindrop.collectionId)?.title}
                  </div>
                ) : (
                  <Link
                    to={`https://app.raindrop.io/my/${getCollection(raindrop.collectionId)?.raindropCollectionId}`}
                    target='_blank'
                    rel="noreferrer"
                    className=" text-gray-500 border-gray-300 border rounded-md px-2 py-1 text-xs items-center gap-2 inline-flex hover:bg-gray-100"
                  >
                    <FolderClosedIcon className="w-[15px] h-[15px]" />
                    {getCollection(raindrop.collectionId)?.title}
                  </Link>
                )
              )}
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
});
RaindropCard.displayName = 'RaindropCard';

const LockedRaindropCard = memo(({ raindrop }: {
  raindrop: RaindropData;
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [imageError, setImageError] = useState(false);

  const FallbackImage = () => (
    <div className="absolute top-0 left-0 w-full h-full bg-gray-200 rounded-t-lg flex items-center justify-center">
      <div className="text-center text-gray-300">
        <ImageOff className="w-8 h-8 mx-auto" />
      </div>
    </div>
  );

  return (
    <Card
      className="flex flex-col relative cursor-pointer select-none"
      data-testid="locked-raindrop-card"
      onClick={() => setShowOverlay(!showOverlay)}
    >
      {/* ブラーレイヤー */}
      <div className="blur-[3px] pointer-events-none">
        <CardHeader className="p-0">
          <div
            className="relative w-full bg-gray-300 rounded-t-lg"
            style={{ aspectRatio: '16/9' }}
          >
            {raindrop.cover && !imageError ? (
              <img
                src={raindrop.cover}
                alt=""
                className="absolute top-0 left-0 w-full h-full object-cover rounded-t-lg"
                onError={() => setImageError(true)}
                loading="lazy"
              />
            ) : (
              <FallbackImage />
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-grow flex flex-col px-4 pt-4 pb-3">
          <div className="font-semibold text-[15px] line-clamp-2">{raindrop.title}</div>
          <div className="text-sm text-gray-400 mt-1">{raindrop.domain}</div>
        </CardContent>
        <CardFooter className="border-t py-2 px-4">
          <div className="flex gap-2 items-center w-full">
            <div className="text-xs text-gray-400">
              {formatDate(new Date(raindrop.created))}
            </div>
          </div>
        </CardFooter>
      </div>

      {/* ロックバッジ（右上） */}
      <div className="absolute top-2 right-2 bg-purple-100 text-purple-600 rounded-full p-1.5 z-10">
        <Lock className="h-3.5 w-3.5" />
      </div>

      {/* クリック時オーバーレイ */}
      {showOverlay && (
        <div className="absolute inset-0 bg-white/90 rounded-lg flex flex-col items-center justify-center p-4 z-20">
          <Lock className="h-8 w-8 text-gray-400 mb-3" />
          <p className="text-sm font-semibold text-gray-700 mb-1">Unlock with Deep Dive</p>
          <p className="text-xs text-gray-500 mb-4 text-center">
            Get up to {SUBSCRIPTION.PLANS.PAID.MAX_RAINDROPS_PER_DEW} raindrops per dew
          </p>
          <Link to="/pricing" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="bg-gray-800 hover:bg-gray-700 text-white text-xs">
              Upgrade
              <ArrowRightIcon className="ml-1 h-3 w-3" />
            </Button>
          </Link>
          <p className="text-xs text-gray-400 mt-2">
            ${SUBSCRIPTION.PLANS.PAID.PRICE_MONTHLY_USD}/month
          </p>
        </div>
      )}
    </Card>
  );
});
LockedRaindropCard.displayName = 'LockedRaindropCard';

const DewHeader = memo(({ dews, currentDew, isDemo = false }: {
  dews: DewData[],
  currentDew?: DewData,
  isDemo?: boolean
}) => {
  const buttonText = useMemo(() => {
    return currentDew ? formatDate(new Date(currentDew.generatedAt)) : dews[0]?.generatedAt;
  }, [currentDew, dews]);

  return (
    <>
      <div className="flex items-center justify-center w-full mb-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="text-3xl">
              {buttonText}
              <ChevronDownIcon className="w-3.5 h-3.5 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuLabel className="text-gray-400">Generated At</DropdownMenuLabel>
            <DropdownMenuGroup>
              {dews.map((dew) => (
                <Link key={dew.id}  to={isDemo ? `/demo/${dew.id}` : `/dews/${dew.id}`}>
                  <DropdownMenuItem className={`${currentDew?.id === dew.id ? 'bg-gray-200' : ''}`}>
                      {formatDate(new Date(dew.generatedAt))}
                  </DropdownMenuItem>
                </Link>
              ))}
            </DropdownMenuGroup>
            {!isDemo && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <Link to="/dews">
                    <DropdownMenuItem>
                      All Dews
                      <DropdownMenuShortcut><ArrowRightIcon className="w-4 h-4" /></DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </Link>
                </DropdownMenuGroup>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center justify-center w-full mt-2 mb-2">
        <div className="text-xs text-gray-500">
          Generated At: {currentDew?.generatedAt ? formatDateTime(new Date(currentDew.generatedAt)) : '-'}
        </div>
      </div>
    </>
  );
});
DewHeader.displayName = 'DewHeader';
